import React, { useState, useEffect } from 'react';
import styles from './ThirdPartyChemModal.module.css';
import Modal from './Modal.tsx';
import Spinner from './Spinner.tsx';
import { getThirdPartyChems, saveThirdPartyChem, deleteThirdPartyChem } from '../services/apiThirdPartyChems.ts';
import { getIngredients } from '../services/IngredientsService';
import { toast } from 'react-toastify';

interface BackendIngredient {
  id: number;
  code: string;
  ingredient_name: string;
  cas_number: string;
  product_name: string;
  purpose: string;
}

interface Ingredient {
  id: number;
  code: string;
  ingredientName: string;
  casNumber: string;
  tradeName: string;
  purpose: string;
}

interface ThirdPartyChemMapping {
  customer_id: number;
  chem_code: string;
  ingredients_id: number;
  ingredient?: BackendIngredient;
}

interface ThirdPartyChemModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: number;
  customerName: string;
}

const ThirdPartyChemModal: React.FC<ThirdPartyChemModalProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName,
}) => {
  const [mappings, setMappings] = useState<ThirdPartyChemMapping[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [filteredIngredients, setFilteredIngredients] = useState<Ingredient[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('code');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [newMapping, setNewMapping] = useState<{
    chem_code: string;
    ingredients_id: number;
  }>({
    chem_code: '',
    ingredients_id: 0,
  });

  // Fetch third-party chemical mappings and ingredients
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch mappings and ingredients in parallel
        const [mappingsData, ingredientsData] = await Promise.all([
          getThirdPartyChems(customerId),
          getIngredients()
        ]);
        
        setMappings(mappingsData);
        setIngredients(ingredientsData);
        setFilteredIngredients(ingredientsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, customerId]);

  // Filter and sort ingredients when search text or sort option changes
  useEffect(() => {
    let result = [...ingredients];
    
    // For the dropdown, show unique combinations by code + trade name for selection
    // But keep all ingredients available for mapping
    const uniqueForDropdown = result.reduce((acc: Ingredient[], current) => {
      const existingIndex = acc.findIndex(item => 
        item.code === current.code && 
        item.tradeName && current.tradeName && 
        item.tradeName.toLowerCase() === current.tradeName.toLowerCase()
      );
      
      if (existingIndex === -1) {
        acc.push(current);
      }
      
      return acc;
    }, []);
    
    result = uniqueForDropdown;
    
    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(ing => 
        ing.code.toLowerCase().includes(searchLower) || 
        (ing.tradeName && ing.tradeName.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'code') {
        return a.code.localeCompare(b.code);
      } else if (sortBy === 'tradeName') {
        const tradeNameA = a.tradeName || '';
        const tradeNameB = b.tradeName || '';
        return tradeNameA.localeCompare(tradeNameB);
      }
      return 0;
    });
    
    setFilteredIngredients(result);
  }, [ingredients, searchText, sortBy]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewMapping({
      ...newMapping,
      [name]: value,
    });
  };

  const handleAddMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMapping.chem_code || !newMapping.ingredients_id) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Find the selected ingredient to get its code
      const selectedIngredient = ingredients.find(ing => ing.id === Number(newMapping.ingredients_id));
      if (!selectedIngredient) {
        toast.error('Selected ingredient not found');
        return;
      }
      
      // Find all ingredients with the same code
      const relatedIngredients = ingredients.filter(ing => 
        ing.code === selectedIngredient.code
      );
      
      console.log(`Found ${relatedIngredients.length} ingredients for code: ${selectedIngredient.code}`);
      console.log('Related ingredients:', relatedIngredients.map(ing => `ID: ${ing.id}, Name: ${ing.ingredientName}`));
      
      // Create mappings for each ingredient
      let successCount = 0;
      for (const ingredient of relatedIngredients) {
        try {
          await saveThirdPartyChem(
            customerId,
            newMapping.chem_code,
            ingredient.id
          );
          successCount++;
        } catch (err) {
          console.error(`Failed to map ingredient ${ingredient.id}:`, err);
        }
      }
      
      toast.success(`Successfully mapped ${successCount} ingredient${successCount > 1 ? 's' : ''} to chemical code ${newMapping.chem_code}`);
      
      // Reset form
      setNewMapping({
        chem_code: '',
        ingredients_id: 0,
      });
      
      // Refresh mappings immediately after successful save
      try {
        const updatedMappings = await getThirdPartyChems(customerId);
        setMappings(updatedMappings);
      } catch (refreshErr) {
        console.error('Error refreshing mappings:', refreshErr);
        // Force a complete refresh by re-fetching all data
        try {
          const [mappingsData, ingredientsData] = await Promise.all([
            getThirdPartyChems(customerId),
            getIngredients()
          ]);
          setMappings(mappingsData);
          setIngredients(ingredientsData);
          setFilteredIngredients(ingredientsData);
        } catch (fullRefreshErr) {
          console.error('Error with full refresh:', fullRefreshErr);
          setError('Failed to refresh mappings. Please close and reopen the modal.');
        }
      }
      
    } catch (err) {
      console.error('Error adding mapping:', err);
      toast.error('Failed to add mapping. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (chemCode: string) => {
    try {
      setLoading(true);
      setError(null);
      await deleteThirdPartyChem(customerId, chemCode);
      
      // Refresh mappings immediately after successful delete
      try {
        const updatedMappings = await getThirdPartyChems(customerId);
        setMappings(updatedMappings);
      } catch (refreshErr) {
        console.error('Error refreshing mappings:', refreshErr);
        // Force a complete refresh by re-fetching all data
        try {
          const [mappingsData, ingredientsData] = await Promise.all([
            getThirdPartyChems(customerId),
            getIngredients()
          ]);
          setMappings(mappingsData);
          setIngredients(ingredientsData);
          setFilteredIngredients(ingredientsData);
        } catch (fullRefreshErr) {
          console.error('Error with full refresh:', fullRefreshErr);
          setError('Failed to refresh mappings. Please close and reopen the modal.');
        }
      }
      
      toast.success('Mapping deleted successfully');
    } catch (err) {
      console.error('Error deleting mapping:', err);
      toast.error('Failed to delete mapping. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Find ingredient details by id
  const getIngredientDetails = (id: number) => {
    return ingredients.find(ing => ing.id === id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manage Third Party Chemicals - ${customerName}`}
    >
      <div className={styles.thirdPartyChemContainer}>
        <p className={styles.description}>
          Map third-party chemical codes from the API to ingredients in your system.
          When you select a chemical to map, all ingredients with the same code will be automatically mapped to the chemical code.
          This helps ensure correct ingredient identification in Chemical Disclosures.
        </p>
        
        {loading && (
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        )}
        
        {error && (
          <div className={styles.errorMessage}>{error}</div>
        )}
        
        {!loading && !error && (
          <>
            <form onSubmit={handleAddMapping} className={styles.mappingForm}>
              <div className={styles.formGroup}>
                <label htmlFor="chem_code">Chemical Code</label>
                <select
                  id="chem_code"
                  name="chem_code"
                  value={newMapping.chem_code}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a chemical code</option>
                  {[
                    "A1047", "P1045", "P1046", "P1047", "P1048", "P1049", "P1050", "P1051", "P1052", "P1053", "P1054",
                    "P1055", "P1056", "P1057", "P1058", "P1059", "P1060", "P1061", "P1062", "P1063", "P1064",
                    "P1065", "P1066", "P1067", "P1068", "P1069", "P1070", "P1071", "P1072", "P1073", "P1074",
                    "P1075", "P1076", "P1077", "P1078", "P1079", "P1080", "P1081", "P1082", "P1083", "P1084",
                    "P1085", "P1086", "P1087", "P1088", "P1089", "P1090", "P1091", "P1092", "P1093", "P1094",
                    "P1095", "CP1001", "CP1002", "CP1003", "CP1004", "CP1005", "CP1006", "CP1007", "CP1008", "CP1009",
                    "CP1010", "CP1011", "CP1012", "CP1013", "CP1014", "CP1015", "CP1016", "CP1017", "CP1018", "CP1019",
                    "CP1020", "CP1021", "CP1022", "CP1023", "CP1024", "CP1025", "CP1026", "CP1027", "CP1028", "CP1029",
                    "CP1030", "CP1031", "CP1032", "CP1033", "CP1034", "CP1035", "CP1036", "CP1037", "CP1038", "CP1039",
                    "CP1040", "CP1041", "CP1042", "CP1043", "CP1044", "CP1045", "CP1046", "CP1047", "CP1048", "CP1049",
                    "CP1050", "CP1051", "CP1052", "CP1053", "CP1054", "CP1055", "CP1056", "CP1057", "CP1058", "CP1059",
                    "CP1060", "CP1061", "CP1062", "CP1063", "CP1064", "CP1065", "CP1066", "CP1067", "CP1068", "CP1069",
                    "CP1070", "CP1071", "CP1072", "CP1073", "CP1074", "CP1075", "CP1076", "CP1077", "CP1078", "CP1079",
                    "CP1080", "CP1081", "CP1082", "CP1083", "CP1084", "CP1085", "CP1086", "CP1087", "CP1088", "CP1089",
                    "CP1090", "CP1091", "CP1092", "CP1093", "CP1094", "CP1095", "CP1096", "CP1097", "CP1098", "CP1099",
                    "CP1100", "CP1101", "CP1102", "CP1103", "CP1104", "CP1105", "CP1106", "CP1107", "CP1108", "CP1109",
                    "CP1110", "CP1111", "CP1112", "CP1113", "CP1114", "CP1115", "CP1116", "CP1117", "CP1118", "CP1119",
                    "CP1120", "CP1121", "CP1122", "CP1123", "CP1124", "CP1125"
                  ].map(code => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="ingredients_id">Map to Chemical</label>
                <div className={styles.searchFilterContainer}>
                  <input
                    type="text"
                    placeholder="Search chemicals..."
                    className={styles.searchInput}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <select 
                    className={styles.sortSelect}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="code">Sort by Code</option>
                    <option value="tradeName">Sort by Trade Name</option>
                  </select>
                </div>
                <select
                  id="ingredients_id"
                  name="ingredients_id"
                  value={newMapping.ingredients_id.toString()}
                  onChange={handleInputChange}
                  required
                >
                  <option value="0">Select a chemical</option>
                  {filteredIngredients.map(ingredient => (
                    <option key={ingredient.id} value={ingredient.id.toString()}>
                      {ingredient.code} - {ingredient.tradeName || 'No Trade Name'}
                    </option>
                  ))}
                </select>
              </div>
              
              <button type="submit" className={styles.addButton} disabled={loading}>
                Add Mapping
              </button>
            </form>
            
            <div className={styles.tableContainer}>
              <h3>Current Mappings ({mappings.length})</h3>
              
              {mappings.length === 0 ? (
                <p>No mappings found. Add your first mapping above.</p>
              ) : (
                <table className={styles.table} key={`mappings-table-${mappings.length}`}>
                  <thead>
                    <tr>
                      <th>Chemical Code</th>
                      <th>Trade Name</th>
                      <th>Purpose</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((mapping, index) => {
                      const frontendIngredient = getIngredientDetails(mapping.ingredients_id);
                      const backendIngredient = mapping.ingredient;
                      
                      // Get the trade name
                      let tradeName = 'N/A';
                      if (frontendIngredient) {
                        tradeName = frontendIngredient.tradeName;
                      } else if (backendIngredient) {
                        tradeName = backendIngredient.product_name;
                      }
                      
                      // Get the purpose
                      let purpose = 'N/A';
                      if (frontendIngredient) {
                        purpose = frontendIngredient.purpose;
                      } else if (backendIngredient) {
                        purpose = backendIngredient.purpose;
                      }
                      
                      return (
                        <tr key={`${mapping.customer_id}-${mapping.chem_code}-${mapping.ingredients_id}-${index}`}>
                          <td>{mapping.chem_code}</td>
                          <td>{tradeName}</td>
                          <td>{purpose}</td>
                          <td>
                            <button
                              className={styles.deleteButton}
                              onClick={() => handleDeleteMapping(mapping.chem_code)}
                              disabled={loading}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        
        <div className={styles.formActions}>
          <button type="button" onClick={onClose} className={styles.cancelButton}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ThirdPartyChemModal;
